/**
 * 语音翻译 composable
 * 集成 Azure Speech Translation SDK，实现一源多目标实时翻译
 *
 * Azure 语音翻译的 AddTargetLanguage 需使用目标语言简写（如 en、zh-Hans），
 * 与后端 cr_language 的 NA_NAME / 接口返回的 shortCode、azureTargetCode 一致。
 * 优先使用 options.targetLanguageAzureCodes（来自接口），否则按 languageCode 回退。
 */

import { ref, onUnmounted } from 'vue'
import { appendFragment, getSpeechToken, publishPreview } from '../api'
import {
  applySpeechTranslationSegmentation,
  buildTranslationsFromAzureCodes,
  resolveEffectiveAzureTargetCodes,
} from '../utils/azure-speech-sdk-shared'

export interface UseSpeechTranslationOptions {
  appId: string
  sourceLanguage: string
  targetLanguages: string[]
  /** 与 targetLanguages 一一对应的 Azure 目标语言代码（接口 shortCode/azureTargetCode），用于 addTargetLanguage */
  targetLanguageAzureCodes?: string[]
  region?: string
  /** 可选：传入已获取的麦克风流，避免与视图层双路麦克风冲突 */
  audioStream?: MediaStream
}

export function useSpeechTranslation(options: UseSpeechTranslationOptions) {
  const isRecognizing = ref(false)
  const error = ref<string | null>(null)
  const lastSource = ref('')
  const lastTranslations = ref<Record<string, string>>({})
  /** 已识别的翻译片段累积列表，供输出面板展示 */
  const fragments = ref<Array<{ sourceText: string; translations: Record<string, string> }>>([])

  interface RecognizerLike {
    startContinuousRecognitionAsync(): Promise<void>
    stopContinuousRecognitionAsync(): Promise<void>
    close(): void
  }
  let recognizer: RecognizerLike | null = null
  /** 节流推送识别中译文给观众端，避免 recognizing 高频打爆 HTTP */
  let previewFlushTimer: ReturnType<typeof setTimeout> | null = null
  /** 为 false 时不向服务端推 preview/fragments（结束会议后 Redis 已删，推了会 400） */
  let pushEnabled = false

  function schedulePreviewPush() {
    if (previewFlushTimer) clearTimeout(previewFlushTimer)
    previewFlushTimer = setTimeout(() => {
      previewFlushTimer = null
      if (!pushEnabled) return
      void publishPreview(options.appId, {
        sourceText: lastSource.value,
        translations: { ...lastTranslations.value }
      }).catch(() => {
        //
      })
    }, 100)
  }

  function cancelPreviewPushSchedule() {
    if (previewFlushTimer) {
      clearTimeout(previewFlushTimer)
      previewFlushTimer = null
    }
  }

  async function start() {
    try {
      error.value = null
      fragments.value = []
      cancelPreviewPushSchedule()
      pushEnabled = true
      const { token, region } = await getSpeechToken()
      if (!token || !region) {
        pushEnabled = false
        error.value = '无法获取语音服务 token'
        return
      }

      const speechsdk = await import('microsoft-cognitiveservices-speech-sdk')
      const config = speechsdk.SpeechTranslationConfig.fromAuthorizationToken(token, region)
      config.speechRecognitionLanguage = options.sourceLanguage
      const effectiveAzureCodes = resolveEffectiveAzureTargetCodes(
        options.targetLanguages,
        options.targetLanguageAzureCodes,
      )
      const azureTargets = new Set<string>()
      for (const code of effectiveAzureCodes) {
        if (code && !azureTargets.has(code)) {
          azureTargets.add(code)
          config.addTargetLanguage(code)
        }
      }

      applySpeechTranslationSegmentation(config, speechsdk)

      const audioConfig = options.audioStream
        ? speechsdk.AudioConfig.fromStreamInput(options.audioStream)
        : speechsdk.AudioConfig.fromDefaultMicrophoneInput()
      recognizer = new speechsdk.TranslationRecognizer(config, audioConfig)

      recognizer.recognizing = (_: unknown, e: { result: { reason: number; text: string; translations: Map<string, string> } }) => {
        if (!pushEnabled) return
        if (e.result.reason === speechsdk.ResultReason.TranslatingSpeech) {
          const r = e.result
          lastSource.value = r.text
          lastTranslations.value = buildTranslationsFromAzureCodes(
            r.translations,
            options.targetLanguages,
            effectiveAzureCodes
          )
          schedulePreviewPush()
        }
      }

      recognizer.recognized = async (_: unknown, e: { result: { reason: number; text: string; translations: Map<string, string>; errorDetails?: string } }) => {
        if (!pushEnabled) return
        cancelPreviewPushSchedule()
        const r = e.result
        if (r.reason === speechsdk.ResultReason.Canceled) {
          const msg = (r as { errorDetails?: string }).errorDetails || '识别已取消'
          if (import.meta.env.DEV) console.warn('[SpeechTranslation] recognized Canceled:', msg)
          error.value = msg
          return
        }
        if (r.reason === speechsdk.ResultReason.NoMatch) {
          if (import.meta.env.DEV) console.warn('[SpeechTranslation] recognized NoMatch')
          return
        }
        if (r.reason === speechsdk.ResultReason.TranslatedSpeech) {
          const source = r.text
          const trans = buildTranslationsFromAzureCodes(
            r.translations,
            options.targetLanguages,
            effectiveAzureCodes
          )
          if (import.meta.env.DEV) {
            console.log('[SpeechTranslation] TranslatedSpeech', {
              reason: r.reason,
              text: source,
              azureCodesUsed: effectiveAzureCodes,
              matchedCount: Object.keys(trans).length
            })
          }
          if (source) {
            fragments.value.push({ sourceText: source, translations: trans })
            if (!pushEnabled) return
            try {
              await appendFragment(options.appId, { sourceText: source, translations: trans })
            } catch {
              // ignore
            }
          }
        }
      }

      await recognizer.startContinuousRecognitionAsync()
      isRecognizing.value = true
    } catch (e: unknown) {
      pushEnabled = false
      // 优先使用后端返回的 detail（如 503「无法获取语音服务 token」），其次 axios/Error 信息
      const err = e as { response?: { data?: { detail?: string } }; message?: string }
      const detail = err?.response?.data?.detail
      error.value =
        typeof detail === 'string' ? detail : e instanceof Error ? e.message : '启动失败'
    }
  }

  async function stop() {
    pushEnabled = false
    cancelPreviewPushSchedule()
    if (recognizer) {
      await recognizer.stopContinuousRecognitionAsync()
      recognizer.close()
      recognizer = null
    }
    isRecognizing.value = false
    try {
      await publishPreview(options.appId, { sourceText: '', translations: {} })
    } catch {
      // 会话可能已结束；静默忽略，避免结束流程再报 400
    }
  }

  onUnmounted(() => {
    stop()
  })

  return {
    isRecognizing,
    error,
    lastSource,
    lastTranslations,
    fragments,
    start,
    stop
  }
}
